<template>
  <v-layout
    row
    wrap
  >
    <v-flex
      xs12
      pa-0
    >
      <v-btn
        :disabled="maxDepth <= depth"
        small
        flat
        color="primary lighten-1"
        @click="handleClickAddGroup"
      >
        + {{ $t(addGroupText) }}
      </v-btn>
      <v-btn
        :disabled="maxDepth + 1 <= depth"
        small
        flat
        color="primary"
        @click="handleClickAddRule"
      >
        + {{ $t(addRuleText) }}
      </v-btn>
      <v-btn
        :disabled="maxDepth + 1 <= depth"
        small
        flat
        color="primary"
        @click="handleClickAddSubset"
      >
        + {{ $t(addSubsetText) }}
      </v-btn>
      <v-tooltip bottom>
        <v-btn
          v-if="depth > 1"
          slot="activator"
          icon
          @click="handleClickRemoveGroup(position)"
        >
          <v-icon color="grey">
            cancel
          </v-icon>
        </v-btn>
        <span>{{ $t(removeGroupText) }}</span>
      </v-tooltip>
    </v-flex>
    <v-flex xs12>
      <template v-if="query.subsets && query.subsets.length">
        <v-layout row>
          <v-flex class="mb-3">
            <span>{{ $t('for the following group of conditions where') }} </span>
          </v-flex>
        </v-layout>
        <v-layout
          v-for="(subset, index) in query.subsets"
          :key="`group-${subset.type}-${index}`"
          row
          wrap
          class="ml-5"
        >
          <v-flex
            xs12
            class="my-0 py-0"
          >
            <v-layout class="ml-0">
              <v-layout
                row
                wrap
              >
                <v-flex>
                  <query-builder-subset
                    :ref="`rule${index}`"
                    :index="`${position}${depth}${index}`"
                    :position="index"
                    :operators="operators"
                    :operands="operands.filter(o => o.usage === 'subset')"
                    :query="subset"
                    @change-operand="handleChangeOperand"
                    @change-operator="handleChangeOperator"
                    @change-value="handleChangeValue"
                    @enter-keyup="handleEnterKeyUp"
                  />
                </v-flex>
              </v-layout>
              <v-tooltip bottom>
                <v-btn
                  slot="activator"
                  icon
                  class="mt-2"
                  @click="handleClickRemoveSubset(index)"
                >
                  <v-icon color="grey">
                    cancel
                  </v-icon>
                </v-btn>
                <span>{{ $t(removeRuleText) }}</span>
              </v-tooltip>
            </v-layout>
          </v-flex>
        </v-layout>
      </template>

      <v-flex
        v-if="query.children.length"
        class="mb-3 pt-0 mt-0"
      >
        <span>{{ $t('If') }} </span>

        <v-btn-toggle
          v-model="query.logicalOperator"
          class="transparent mx-2"
          mandatory
        >
          <v-btn
            value="Any"
            small
            flat
          >
            {{ $t('any') }}
          </v-btn>

          <v-btn
            value="All"
            small
            flat
          >
            {{ $t('all') }}
          </v-btn>
        </v-btn-toggle>

        <span>{{ $t('of the following are true:') }} </span>
      </v-flex>

      <v-layout
        v-if="!query.children"
        row
        wrap
        class="ml-5"
      >
        <v-flex
          xs12
          class="my-0 py-0"
        >
          <v-layout
            class="ml-0"
          >
            <query-builder-rule
              :ref="`rule${index}`"
              :index="`${position}${depth}${index}`"
              :position="index"
              :operators="operators"
              :operands="operands.filter(o => o.usage === 'rule')"
              :query="query"
              @change-operand="handleChangeOperand"
              @change-operator="handleChangeOperator"
              @change-value="handleChangeValue"
              @enter-keyup="handleEnterKeyUp"
            />
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                class="mt-2"
                @click="handleClickRemoveRule(index)"
              >
                <v-icon color="grey">
                  cancel
                </v-icon>
              </v-btn>
              <span>{{ $t(removeRuleText) }}</span>
            </v-tooltip>
          </v-layout>
        </v-flex>
      </v-layout>

      <template v-if="query.children">
        <v-layout
          v-for="(child, index) in query.children"
          :key="`group-${child.type}-${index}`"
          row
          wrap
          class="ml-5"
        >
          <v-flex
            xs12
            class="my-0 py-0"
          >
            <v-divider v-if="child.query.children"/>
            <query-builder-group
              v-if="child.query.children"
              :ref="`group${index}`"
              :operators="operators"
              :operands="operands"
              :depth="depth + 1"
              :position="index"
              :max-depth="maxDepth"
              :query="child.query"
              :parent="query"
              class="my-0 py-0"
              @add-group="handleAddGroup"
              @add-rule="handleAddRule"
              @add-subset="handleAddSubset"
              @remove-group="handleRemoveGroup"
              @remove-rule="handleRemoveRule"
              @remove-subset="handleRemoveSubset"
              @change-operand="handleChangeOperand"
              @change-operator="handleChangeOperator"
              @change-value="handleChangeValue"
            />
            <v-layout
              v-if="!child.query.children"
              class="ml-0"
            >
              <query-builder-rule
                :ref="`rule${index}`"
                :index="`${position}${depth}${index}`"
                :position="index"
                :operators="operators"
                :operands="operands.filter(o => o.usage === 'rule')"
                :query="child.query"
                @change-operand="handleChangeOperand"
                @change-operator="handleChangeOperator"
                @change-value="handleChangeValue"
                @enter-keyup="handleEnterKeyUp"
              />
              <v-tooltip bottom>
                <v-btn
                  slot="activator"
                  icon
                  class="mt-2"
                  @click="handleClickRemoveRule(index)"
                >
                  <v-icon color="grey">
                    cancel
                  </v-icon>
                </v-btn>
                <span>{{ $t(removeRuleText) }}</span>
              </v-tooltip>
            </v-layout>
          </v-flex>
        </v-layout>
      </template>
    </v-flex>
  </v-layout>
</template>

<script>
import QueryBuilderGroup from './QueryBuilderGroup.vue'
import QueryBuilderRule from './QueryBuilderRule.vue'
import QueryBuilderSubset from '@/modules/Rules/components/QueryBuilderSubset'

export default {
  name: 'QueryBuilderGroup',

  components: {
    QueryBuilderSubset,
    QueryBuilderGroup,
    QueryBuilderRule
  },

  props: {
    operators: {
      type: Array,
      required: true
    },
    operands: {
      type: Array,
      required: true
    },
    query: {
      type: Object,
      required: true
    },
    depth: {
      type: Number,
      default: 1
    },
    maxDepth: {
      type: Number,
      default: 3
    },
    parent: {
      type: Object,
      default () {
        return null
      }
    },
    position: {
      type: Number,
      default: 0
    },
    addGroupText: {
      type: String,
      default: function () {
        return 'group'
      }
    },
    addRuleText: {
      type: String,
      default: function () {
        return 'condition'
      }
    },
    addSubsetText: {
      type: String,
      default: function () {
        return 'subset'
      }
    },
    removeGroupText: {
      type: String,
      default: function () {
        return 'Remove group'
      }
    },
    removeRuleText: {
      type: String,
      default: function () {
        return 'Remove condition'
      }
    }
  },

  methods: {
    handleAddGroup () {
      this.$emit('add-group', ...arguments)
    },

    handleAddRule () {
      this.$emit('add-rule', ...arguments)
    },

    handleAddSubset () {
      this.$emit('add-subset', ...arguments)
    },

    handleRemoveGroup () {
      this.$emit('remove-group', ...arguments)
    },

    handleRemoveRule () {
      this.$emit('remove-rule', ...arguments)
    },

    handleRemoveSubset () {
      this.$emit('remove-subset', ...arguments)
    },

    handleClickAddGroup () {
      const group = {
        query: {
          logicalOperator: 'All',
          subsets: [],
          children: [
            {
              query: {
                selectedOperator: '',
                rule: '',
                value: '',
                type: 'text'
              }
            }
          ]
        }
      }
      if (!this.query.children) {
        this.query.children = []
      }
      this.query.children.push(group)
      this.$emit('add-group', group, this.query, this.parent)
    },

    handleClickAddRule () {
      const query = {
        query: {
          selectedOperator: '',
          rule: '',
          value: '',
          type: 'text'
        }
      }
      if (!this.query.children) {
        this.query.children = []
      }
      this.query.children.push(query)
      this.$emit('add-rule', query, this.query, this.parent)
    },

    handleClickAddSubset () {
      const query = {
        selectedOperator: '',
        rule: '',
        value: '',
        type: 'text',
        name: ''
      }

      if (!this.query.subsets) {
        this.query.subsets = []
      }
      this.query.subsets.push(query)
      this.$emit('add-subset', query, this.query, this.parent)
    },

    handleClickRemoveRule (index) {
      const deleted = this.query.children.splice(index, 1)[0]
      this.$emit('remove-rule', deleted, index, this.query)
    },

    handleClickRemoveSubset (index) {
      const deleted = this.query.subsets.splice(index, 1)[0]
      this.$emit('remove-subset', deleted, index, this.query)
    },

    handleClickRemoveGroup (index) {
      const query = this.parent.children.splice(index, 1)[0]
      this.$emit('remove-group', query, this.position)
    },

    handleChangeOperand (operand, query, positions) {
      this.$emit('change-operand', operand, query, this.query, positions)
    },

    handleChangeOperator (operator, query, positions) {
      if (this.position !== 0) {
        positions.push(this.position)
      }
      this.$emit('change-operator', operator, query, this.query, positions)
    },

    handleChangeValue (value, query, positions) {
      if (this.position !== 0) {
        positions.push(this.position)
      }
      this.$emit('change-value', value, query, this.query, positions)
    },

    handleEnterKeyUp (event) {
      this.$emit('enter-keyup', event)
    }
  }
}
</script>

<style scoped>
.nested {
  outline: 1px solid blue;
}
</style>
